import { Link } from "react-router";
import { motion } from "framer-motion";
import { ArrowRight, Play } from "lucide-react";
import { PATREON_URL, CLUB_PERKS } from "@/lib/club";
import { WORDS } from "../concept-content";
import { nextClasses } from "../ClubRichThread";
import { useVideoCards, FOLLOW } from "../ConceptBits";
import { Band, CLUB, EASE, Head, InkHeart, U, VIEW, datePart, rng } from "./bits";
import "./SectionsA.css";

/* 01 Making, 02 Club, 03 Kind words. */

/* No arches here (team mandate: only the hero keeps one). The three video
   cards each get a different sewing cut: a pinking-shear swatch, a button, and
   a torn scrap taped down. */

const pct = (n: number) => `${+n.toFixed(2)}%`;

/** clip-path for a rectangle cut with pinking shears: zigzag on the top and
    bottom (and the sides too when `sides` is set). Depths are % of each axis. */
function pinked(teeth = 16, depth = 2.2, sides = 0, sideDepth = depth) {
  const pts: string[] = [];
  for (let i = 0; i <= teeth; i++) pts.push(`${pct((i / teeth) * 100)} ${pct(i % 2 ? depth : 0)}`);
  for (let i = 1; i < sides; i++) pts.push(`${pct(i % 2 ? 100 - sideDepth : 100)} ${pct((i / sides) * 100)}`);
  for (let i = teeth; i >= 0; i--) pts.push(`${pct((i / teeth) * 100)} ${pct(100 - (i % 2 ? depth : 0))}`);
  for (let i = sides - 1; i > 0; i--) pts.push(`${pct(i % 2 ? sideDepth : 0)} ${pct((i / sides) * 100)}`);
  return `polygon(${pts.join(", ")})`;
}

/** clip-path for a scrap torn along the top and bottom. */
function torn(seed: number, amp = 2.4) {
  const r = rng(seed), top: string[] = [], bottom: string[] = [];
  for (let x = 0; x <= 100; x += 5) { top.push(`${x}% ${pct(r() * amp)}`); bottom.push(`${100 - x}% ${pct(100 - r() * amp)}`); }
  return `polygon(${[...top, ...bottom].join(", ")})`;
}

const CUTS = [
  { kind: "pinked", clip: pinked(14, 2.2) },
  { kind: "button", clip: undefined },
  { kind: "scrap", clip: torn(41) },
] as const;

export function Making() {
  const cards = useVideoCards(3);
  return <section id="making" className="p2-sec p2-making" aria-labelledby="p2-making-title">
    <div className="p2-wrap">
      <div className="p2-row">
        <Head n="01" kicker="Fresh off the machine" id="p2-making-title">What I’m <span className="p2-pink">making</span> <U>now.</U></Head>
        <Link to="/videos" className="p2-btn p2-btn-line p2-row-btn">All videos <ArrowRight size={18} aria-hidden="true" /></Link>
      </div>

      <div className="p2-making-grid">
        <svg className="p2-making-thread" viewBox="0 0 1000 200" preserveAspectRatio="none" aria-hidden="true"><path d="M-10 150 C 120 40, 230 40, 330 120 S 560 200, 680 90 S 900 20, 1010 110" /></svg>
        {cards.map((c, i) => { const cut = CUTS[i % CUTS.length]; return <motion.a key={c.id} className={`p2-vid p2-vid-${cut.kind}`} href={c.url} target="_blank" rel="noreferrer"
          initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={VIEW} transition={{ delay: i * 0.12, duration: 0.6, ease: EASE }}>
          <span className="p2-vid-shot">
            <i className="p2-vid-block" style={{ clipPath: cut.clip }} aria-hidden="true" />
            <span className="p2-vid-img" style={{ clipPath: cut.clip }}><img src={c.img} alt="" style={{ objectPosition: c.pos }} /></span>
            {cut.kind === "button" && <svg className="p2-vid-holes" viewBox="0 0 100 100" aria-hidden="true"><circle cx="50" cy="50" r="46.5" /></svg>}
            {cut.kind === "scrap" && <i className="p2-vid-tape" aria-hidden="true" />}
            <b><Play size={18} fill="currentColor" aria-hidden="true" /></b>
          </span>
          <span className="p2-tag">{c.tag}</span>
          <span className="p2-vid-title">{c.title}</span>
          <span className="p2-pen p2-vid-go">watch it <ArrowRight size={16} aria-hidden="true" /></span>
        </motion.a>; })}

        <motion.div className="p2-follow" initial={{ opacity: 0, y: 30, rotate: 2 }} whileInView={{ opacity: 1, y: 0, rotate: -3 }} viewport={VIEW} transition={{ delay: 0.4, type: "spring", stiffness: 120, damping: 15 }}>
          <figure className="p2-follow-photo">
            <img src="/chelsea-and-cosmo.jpg" alt="Cosmo the cat on Chelsea’s lap at the sewing machine" />
            <figcaption className="p2-pen">Cosmo, quality control</figcaption>
          </figure>
          <p className="p2-pen p2-follow-title">Follow along <InkHeart delay={0.8} /></p>
          <ul>{FOLLOW.map(f => <li key={f.s.key}>
            <a href={f.s.url} target="_blank" rel="noreferrer">
              <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="2" y="2" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.6" /><path className="p2-tick" d="M6 12 L10 17 L20 3" /></svg>
              <span>{f.s.platform}</span><small>{f.s.handle}</small>
            </a>
          </li>)}</ul>
        </motion.div>
      </div>
    </div>
  </section>;
}

const YARDAGE = [["Live classes", "2 a month"], ["Replays", "Every one"], ["Skill level", "Any"], ["Sizes", "Everybody"]];

export function Club() {
  const dates = nextClasses(2);
  return <Band id="club" color="var(--k-strong)" seed={61} className="p2-club" label="p2-club-title">
    <div className="p2-wrap p2-club-grid">
      <div className="p2-club-copy">
        <Head n="02" kicker="Loose Threads Club · my Patreon" id="p2-club-title">Sew with me, <span className="p2-yellow">twice a month.</span></Head>
        <p className="p2-club-lede">Every month we pick one bigger project and I teach it live, twice. Ask questions, tackle the tricky parts together, and sew next to people who get it.</p>
        <p className="p2-club-next"><span>Next classes</span>{dates.map(d => datePart(d, { weekday: "short", month: "short", day: "numeric" })).join("  ·  ")} · 7 PM MT</p>
        <div className="p2-actions p2-club-actions">
          <a href={PATREON_URL} target="_blank" rel="noreferrer" className="p2-btn p2-btn-yellow">Join the club <ArrowRight size={18} aria-hidden="true" /></a>
          <Link to={CLUB} className="p2-btn p2-btn-ghost">See what’s inside</Link>
        </div>
        <p className="p2-pen p2-club-pen">Can’t make it live? Every class is recorded. <InkHeart delay={1} /></p>
        <p className="p2-club-foot">Just want to cheer me on? <a href={PATREON_URL} target="_blank" rel="noreferrer">Become a Thread Friend for $3/month.</a></p>
      </div>

      <div className="p2-env-wrap">
        <motion.div className="p2-tissue" aria-hidden="true" initial={{ opacity: 0, rotate: 0 }} whileInView={{ opacity: 1, rotate: 7 }} viewport={VIEW} transition={{ delay: 0.3, duration: 0.8, ease: EASE }}>
          <svg viewBox="0 0 300 400" preserveAspectRatio="none"><path d="M30 30 L250 18 L274 200 L240 380 L40 372 L20 200 Z" /><path className="p2-grain" d="M150 90 V310 M136 108 L150 90 L164 108 M136 292 L150 310 L164 292" /></svg>
          <span>Piece A · Cut 2 · Fold</span>
        </motion.div>
        <motion.article className="p2-env" aria-label="Loose Threads Club, $25 a month" initial={{ opacity: 0, y: 40, rotate: 2 }} whileInView={{ opacity: 1, y: 0, rotate: -3 }} viewport={VIEW} transition={{ type: "spring", stiffness: 110, damping: 16 }}>
          <header className="p2-env-top">
            <b>LTC</b>
            <span><small>Pattern no. 025</small>Loose Threads Club</span>
            <em>Sew-along<br />all levels</em>
          </header>
          <div className="p2-env-body">
            <figure className="p2-env-photo">
              <span style={{ clipPath: pinked(12, 1.8, 15, 2.2) }}><img src="/chelsea-rainbow-portrait.jpg" alt="Chelsea smiling and waving during a live class" /></span>
              <i className="p2-env-live"><b />Live</i>
              <figcaption className="p2-pen">Tuesday nights <InkHeart delay={1.2} /></figcaption>
            </figure>
            <div className="p2-env-info">
              <p className="p2-env-price"><b>$25</b>/month</p>
              <p className="p2-env-label">Pattern includes</p>
              <ol>{CLUB_PERKS.map((p, i) => <li key={p.title}><span>{"ABCD"[i]}</span>{p.title}</li>)}</ol>
            </div>
          </div>
          <dl className="p2-env-yard">{YARDAGE.map(([k, v]) => <div key={k}><dt>{k}</dt><dd>{v}</dd></div>)}</dl>
        </motion.article>
      </div>
    </div>
  </Band>;
}

const LABELS = ["card", "pop", "strong"] as const;

export function KindWords() {
  return <Band color="var(--k-soft)" seed={83} className="p2-words" label="p2-words-title" top={false}>
    <div className="p2-wrap">
      <Head n="03" kicker="Kind words" id="p2-words-title" className="p2-head-center">People who sew <span className="p2-words-hi"><U>with me.</U></span></Head>
      <div className="p2-words-grid">
        {WORDS.map((w, i) => <motion.figure key={w.name} className={`p2-label p2-label-${LABELS[i]}`}
          initial={{ opacity: 0, y: 30, rotate: 0 }} whileInView={{ opacity: 1, y: 0, rotate: [-3, 2, -1.5][i] }} viewport={VIEW} transition={{ delay: i * 0.12, duration: 0.6, ease: EASE }}>
          <blockquote className="p2-pen">“{w.quote}”</blockquote>
          <figcaption><b>{w.name}</b>{w.tag}</figcaption>
        </motion.figure>)}
      </div>
    </div>
  </Band>;
}
